import React, { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "context/auth-context";
import Surveystreak1 from "../../../assets/SideBarIcons/Surveystreakicons/Surveystreak1.svg";
import Gift from "../../../assets/SideBarIcons/Surveystreakicons/Gift.svg";
import trophy from "../../../assets/SideBarIcons/Surveystreakicons/Trophy.svg";
import greenfire from "../../../assets/SideBarIcons/Surveystreakicons/greenFire.svg";
import { getStreakCountApi } from "./Surveystreak.api";
import { weeks } from "./Surveystreak.interface";
import { toastrSuccess } from "lib/toastr";
import { useTranslation } from "react-i18next";

function Surveystreak(showSurveystreak, setShowSurveystreak) {
  const { auth } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowSurveystreak(false);
      }
    };

    if (showSurveystreak) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSurveystreak, setShowSurveystreak]);

  const getSurveyvirtualwallet = useQuery(
    ["streakdata"],
    () => getStreakCountApi(auth?.panelistId, 0),
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleCompletionCheck = () => {
    if (getSurveyvirtualwallet?.data?.completeCount === 7) {
      toastrSuccess(t("steak.notification.Days"));
      queryClient.invalidateQueries(["streakdata"]);
    }
  };

  useEffect(() => {
    handleCompletionCheck();
  }, [getSurveyvirtualwallet.data]);

  const sliceWeeksByStartDay = (startOfStreakDay: string) => {
    const startDayIndex = weeks.findIndex(
      (day) => day.days.substring(0, 3) === startOfStreakDay?.substring(0, 3)
    );
    if (startDayIndex !== -1) {
      return weeks.slice(startDayIndex).concat(weeks.slice(0, startDayIndex));
    } else {
      return weeks;
    }
  };

  const slicedWeeks = sliceWeeksByStartDay(getSurveyvirtualwallet?.data?.streakStartDay);

  const completeCount = getSurveyvirtualwallet?.data?.completeCount;
  const hasStreak = completeCount !== undefined && completeCount !== null && completeCount > 0;
  const longestStreak = getSurveyvirtualwallet?.data?.longestStreak;

  const dayLabels: string[] = hasStreak
    ? slicedWeeks.map((item) => item.days)
    : new Array(7).fill(null).map((_, index) => {
        const day = new Date();
        day.setDate(day.getDate() + index);
        return day.toLocaleDateString("en-US", { weekday: "short" });
      });

  const currentLabel = hasStreak
    ? `${completeCount} ${t("streak.popup.day")}${completeCount !== 1 ? "s" : ""}`
    : `${0} ${t("streak.popup.day")}`;
  const longestLabel =
    longestStreak !== undefined && longestStreak > 0
      ? `${longestStreak} Day${longestStreak !== 1 ? "s" : ""}`
      : `${0} Day`;

  return (
    <div className="fixed z-40 left-2 right-2 sm:left-auto sm:right-4 lg:right-6 top-[calc(var(--zs-topbar-h)+8px)]">
      <div ref={modalRef} className="w-full sm:w-[400px] p-5 zs-popover">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 p-1.5 rounded-lg bg-success-soft">
              <img src={Surveystreak1} alt="" className="w-full h-full" />
            </span>
            <div>
              <p className="text-sm font-semibold font-display text-ink">{t("steak.notification.Streak")}</p>
              <p className="text-xs text-ink-3">{t("steak.notification.Goal")}</p>
            </div>
          </div>
          <span className="zs-badge zs-badge-warning shrink-0">
            <img src={Gift} alt="" className="w-3.5 h-3.5" />
            {t("steak.notification.upto")}
          </span>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mt-5">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className={`aspect-square rounded-md transition-colors ${
                index < (completeCount || 0) ? "bg-success" : "bg-surface-hover border border-line"
              }`}
            ></div>
          ))}
          <div
            className={`aspect-square rounded-md flex items-center justify-center bg-warning-soft border border-warning/20 ${
              completeCount === 7 ? "" : "opacity-50"
            }`}
          >
            <img src={trophy} alt="" className="w-1/2 h-1/2" />
          </div>
          {dayLabels.map((label, index) => (
            <div key={index} className="text-[11px] font-medium text-center text-ink-3">
              {label}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <div className="flex items-center gap-3 p-3 border rounded-lg border-line bg-surface-2">
            <span className="inline-flex items-center justify-center w-8 h-8 p-1.5 rounded bg-success-soft shrink-0">
              <img src={greenfire} alt="" className="w-full h-full" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] text-ink-3 truncate">
                {t("steak.notification.Current")} {t("steak.notification.Streak1")}
              </p>
              <p className="text-sm font-semibold tabular-nums text-ink">{currentLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 border rounded-lg border-line bg-surface-2">
            <span className="inline-flex items-center justify-center w-8 h-8 p-1 rounded bg-primary-soft shrink-0">
              <img src={Surveystreak1} alt="" className="w-full h-full" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] text-ink-3 truncate">
                {t("steak.notification.Longest")} {t("steak.notification.Streak1")}
              </p>
              <p className="text-sm font-semibold tabular-nums text-ink">{longestLabel}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Surveystreak;
